import React from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { Select } from "antd";

export default function StepHybridInput({
  projectValues = {},
  setProjectValues,
  simulationType = "1",
  setSimulationType,
  has220Line = true,
  conductorResistance220 = "",
  setConductorResistance220,
  lineDistance220 = "",
  setLineDistance220,
  useDirectLineLoss220 = false,
  setUseDirectLineLoss220,
  transLineLoss220kv = "",
  setTransLineLoss220kv,

  has400Line = true,
  conductorResistance400 = "",
  setConductorResistance400,
  lineDistance400 = "",
  setLineDistance400,
  useDirectLineLoss400 = false,
  setUseDirectLineLoss400,
  transLineLoss400kv = "",
  setTransLineLoss400kv,

  htAcCableLoss = "",
  ltAcCableLoss = "",
  ltDcCableLoss = "",
  pcsLoss = "",
  idtLoss = "",
  bessIdtNoLoad = "",
  setBessIdtNoLoad,

  windIdtCapacity = "",
  setWindIdtCapacity,
  windIdtNoLoad = "",
  setWindIdtNoLoad,
  windIdtLoad = "",
  setWindIdtLoad,

  has220Transformer = true,
  trans220Capacity = "",
  setTrans220Capacity,
  trans220NoLoad = "",
  setTrans220NoLoad,
  trans220Load = "",
  setTrans220Load,
  useDirectTrans220Loss = false,
  setUseDirectTrans220Loss,
  transLoss220kv = "",
  setTransLoss220kv,

  has400Transformer = true,
  trans400Capacity = "",
  setTrans400Capacity,
  trans400NoLoad = "",
  setTrans400NoLoad,
  trans400Load = "",
  setTrans400Load,
  useDirectTrans400Loss = false,
  setUseDirectTrans400Loss,
  transLoss400kv = "",
  setTransLoss400kv,

  dt33kvPct = "",
  setDt33kvPct,
  bessUnavailability = "",
  setBessUnavailability,
  gridUnavailability = "",
  setGridUnavailability,
  evacuationCapacity = "",
  setEvacuationCapacity,
  bessFeederCapacity = "",
  setBessFeederCapacity,
  minBessStoreEnergy = "",
  setMinBessStoreEnergy,

  windTotalLossesPct = "",
  setWindTotalLossesPct,
  minDispatchPower = "",
  setMinDispatchPower,
  bessDegradation = 1,
  setBessDegradation,
  adjustingFactor = "1",
  setAdjustingFactor,

  onBack,
  onSubmit,
  submitting = false,
}) {
  const isWind = simulationType === "2" || simulationType === 2;

  return (
    <form onSubmit={onSubmit} className="space-y-6 animate-fade-in">
      {/* Losses & Efficiencies */}
      <div className="mt-4 space-y-6">
        <h4 className="form-section-title text-lg font-bold text-slate-800 uppercase tracking-wider pb-1">
          System Losses Settings
        </h4>

        {/* Group 1: Stage 1 Line Parameters & Losses */}
        {has220Line && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col gap-1 text-slate-700">
              <div className="flex items-center gap-1.5 h-4 mb-1">
                <span
                  className={`text-xs font-semibold select-none ${useDirectLineLoss220 ? "text-slate-400" : "text-[#0c2340]"
                    }`}
                >
                  {projectValues.tl_volt_stage_1 || 220}kV Conductor Resistance (Ohm/km)
                </span>
              </div>
              <input
                type="number"
                step="0.00001"
                disabled={useDirectLineLoss220}
                value={conductorResistance220}
                onChange={(e) => setConductorResistance220(e.target.value)}
                placeholder="0.066"
                className={
                  useDirectLineLoss220 ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""
                }
              />
            </div>

            <div className="flex flex-col gap-1 text-slate-700">
              <div className="flex items-center gap-1.5 h-4 mb-1">
                <span
                  className={`text-xs font-semibold select-none ${useDirectLineLoss220 ? "text-slate-400" : "text-[#0c2340]"
                    }`}
                >
                  {projectValues.tl_volt_stage_1 || 220}kV Transmission Line Distance (km)
                </span>
              </div>
              <input
                type="number"
                step="0.001"
                disabled={useDirectLineLoss220}
                value={lineDistance220}
                onChange={(e) => setLineDistance220(e.target.value)}
                placeholder="29.0"
                className={
                  useDirectLineLoss220 ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""
                }
              />
            </div>

            <div className="flex flex-col gap-1 text-slate-700">
              <div className="flex items-center gap-1.5 h-4 mb-1">
                <input
                  type="checkbox"
                  checked={useDirectLineLoss220}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setUseDirectLineLoss220(checked);
                    if (!checked) setTransLineLoss220kv("");
                  }}
                  className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer rounded shrink-0"
                />
                <span
                  onClick={() => {
                    const checked = !useDirectLineLoss220;
                    setUseDirectLineLoss220(checked);
                    if (!checked) setTransLineLoss220kv("");
                  }}
                  className={`text-xs font-semibold cursor-pointer select-none truncate ${!useDirectLineLoss220 ? "text-slate-400" : "text-[#0c2340]"
                    }`}
                >
                  {projectValues.tl_volt_stage_1 || 220}kV Transmission line loss (%)
                </span>
              </div>
              <input
                type="number"
                step="0.01"
                disabled={!useDirectLineLoss220}
                required={useDirectLineLoss220}
                value={transLineLoss220kv}
                onChange={(e) => setTransLineLoss220kv(e.target.value)}
                placeholder="0.50"
                className={
                  !useDirectLineLoss220 ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""
                }
              />
            </div>
          </div>
        )}

        {/* Group 2: Stage 2 Line Parameters & Losses */}
        {has400Line && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
            <div className="flex flex-col gap-1 text-slate-700">
              <div className="flex items-center gap-1.5 h-4 mb-1">
                <span
                  className={`text-xs font-semibold select-none ${useDirectLineLoss400 ? "text-slate-400" : "text-[#0c2340]"
                    }`}
                >
                  {projectValues.tl_volt_stage_2 || 400}kV Conductor Resistance (Ohm/km)
                </span>
              </div>
              <input
                type="number"
                step="0.00001"
                disabled={useDirectLineLoss400}
                value={conductorResistance400}
                onChange={(e) => setConductorResistance400(e.target.value)}
                placeholder="0.066"
                className={
                  useDirectLineLoss400 ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""
                }
              />
            </div>

            <div className="flex flex-col gap-1 text-slate-700">
              <div className="flex items-center gap-1.5 h-4 mb-1">
                <span
                  className={`text-xs font-semibold select-none ${useDirectLineLoss400 ? "text-slate-400" : "text-[#0c2340]"
                    }`}
                >
                  {projectValues.tl_volt_stage_2 || 400}kV Transmission Line Distance (km)
                </span>
              </div>
              <input
                type="number"
                step="0.001"
                disabled={useDirectLineLoss400}
                value={lineDistance400}
                onChange={(e) => setLineDistance400(e.target.value)}
                placeholder="29.0"
                className={
                  useDirectLineLoss400 ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""
                }
              />
            </div>

            <div className="flex flex-col gap-1 text-slate-700">
              <div className="flex items-center gap-1.5 h-4 mb-1">
                <input
                  type="checkbox"
                  checked={useDirectLineLoss400}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setUseDirectLineLoss400(checked);
                    if (!checked) setTransLineLoss400kv("");
                  }}
                  className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer rounded shrink-0"
                />
                <span
                  onClick={() => {
                    const checked = !useDirectLineLoss400;
                    setUseDirectLineLoss400(checked);
                    if (!checked) setTransLineLoss400kv("");
                  }}
                  className={`text-xs font-semibold cursor-pointer select-none truncate ${!useDirectLineLoss400 ? "text-slate-400" : "text-[#0c2340]"
                    }`}
                >
                  {projectValues.tl_volt_stage_2 || 400}kV Transmission line loss (%)
                </span>
              </div>
              <input
                type="number"
                step="0.01"
                disabled={!useDirectLineLoss400}
                required={useDirectLineLoss400}
                value={transLineLoss400kv}
                onChange={(e) => setTransLineLoss400kv(e.target.value)}
                placeholder="0.50"
                className={
                  !useDirectLineLoss400 ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""
                }
              />
            </div>
          </div>
        )}

        {/* Group 3: Cable & Equipment Losses */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
          <div className="flex flex-col gap-1 text-slate-700">
            <div className="flex items-center justify-between h-4 mb-1">
              <span className="text-xs font-semibold text-[#0c2340]">HT AC Cable Loss (%)</span>
            </div>
            <input
              type="number"
              step="0.0001"
              value={htAcCableLoss}
              disabled
              readOnly
              placeholder="0.10"
              className="bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200"
            />
          </div>

          <div className="flex flex-col gap-1 text-slate-700">
            <div className="flex items-center justify-between h-4 mb-1">
              <span className="text-xs font-semibold text-[#0c2340]">LT AC Cable Loss (%)</span>
            </div>
            <input
              type="number"
              step="0.0001"
              value={ltAcCableLoss}
              disabled
              readOnly
              placeholder="0.33"
              className="bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200"
            />
          </div>

          <div className="flex flex-col gap-1 text-slate-700">
            <div className="flex items-center justify-between h-4 mb-1">
              <span className="text-xs font-semibold text-[#0c2340]">LT DC Cable Loss (%)</span>
            </div>
            <input
              type="number"
              step="0.0001"
              value={ltDcCableLoss}
              disabled
              readOnly
              placeholder="0.10"
              className="bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200"
            />
          </div>

          <div className="flex flex-col gap-1 text-slate-700">
            <div className="flex items-center justify-between h-4 mb-1">
              <span className="text-xs font-semibold text-[#0c2340]">PCS Loss (%)</span>
            </div>
            <input
              type="number"
              step="0.0001"
              value={pcsLoss}
              disabled
              readOnly
              placeholder="1.50"
              className="bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200"
            />
          </div>

          <div className="flex flex-col gap-1 text-slate-700">
            <div className="flex items-center justify-between h-4 mb-1">
              <span className="text-xs font-semibold text-[#0c2340]">IDT Loss (%)</span>
            </div>
            <input
              type="number"
              step="0.0001"
              value={idtLoss}
              disabled
              readOnly
              placeholder="1.00"
              className="bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200"
            />
          </div>

          <div className="flex flex-col gap-1 text-slate-700">
            <div className="flex items-center justify-between h-4 mb-1">
              <span className="text-xs font-semibold text-[#0c2340]">BESS IDT No-Load (MW)</span>
            </div>
            <input
              type="number"
              step="0.0001"
              required
              value={bessIdtNoLoad}
              onChange={(e) => setBessIdtNoLoad(e.target.value)}
              placeholder="0.0375"
            />
          </div>
        </div>
      </div>

      {/* Wind IDT Transformer */}
      {isWind && (
        <div className="mt-12 space-y-5">
          <h4 className="form-section-title text-lg font-bold text-slate-800 uppercase tracking-wider pb-1">
            Wind IDT (MVA)
          </h4>
          <div className="grid grid-cols-1 gap-6">
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-3">
                <div className="flex flex-col gap-1 text-slate-700">
                  <label className="text-[10px] font-bold text-slate-500 uppercase">Capacity (MVA)</label>
                  <input
                    type="number"
                    step="0.0001"
                    required={isWind}
                    value={windIdtCapacity}
                    onChange={(e) => setWindIdtCapacity(e.target.value)}
                    placeholder="50.0"
                  />
                </div>
                <div className="flex flex-col gap-1 text-slate-700">
                  <label className="text-[10px] font-bold text-slate-500 uppercase">No-Load (MW)</label>
                  <input
                    type="number"
                    step="0.0001"
                    required={isWind}
                    value={windIdtNoLoad}
                    onChange={(e) => setWindIdtNoLoad(e.target.value)}
                    placeholder="0.05"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Grid Transformers */}
      {(has220Transformer || has400Transformer) && (
        <div className="mt-12 space-y-5">
          <h4 className="form-section-title text-lg font-bold text-slate-800 uppercase tracking-wider pb-1">
            Grid Transformers (MVA)
          </h4>
          <div
            className={`grid grid-cols-1 ${has220Transformer && has400Transformer ? "md:grid-cols-2" : ""
              } gap-6`}
          >
            {has220Transformer && (
              <div className="space-y-2">
                <h5 className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">
                  {projectValues.tl_volt_stage_1 || 220}kV Transformer
                </h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="flex flex-col gap-1 text-slate-700">
                    <div className="flex items-center gap-1.5 h-4 mb-1">
                      <span
                        className={`text-[10px] font-bold uppercase truncate select-none ${useDirectTrans220Loss ? "text-slate-400" : "text-[#0c2340]"
                          }`}
                      >
                        Capacity (MWh)
                      </span>
                    </div>
                    <input
                      type="number"
                      step="0.0001"
                      required={!useDirectTrans220Loss}
                      disabled={useDirectTrans220Loss}
                      value={trans220Capacity}
                      onChange={(e) => setTrans220Capacity(e.target.value)}
                      placeholder="37.5"
                      className={
                        useDirectTrans220Loss
                          ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200"
                          : ""
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-1 text-slate-700">
                    <div className="flex items-center gap-1.5 h-4 mb-1">
                      <span
                        className={`text-[10px] font-bold uppercase truncate select-none ${useDirectTrans220Loss ? "text-slate-400" : "text-[#0c2340]"
                          }`}
                      >
                        No-Load (MW)
                      </span>
                    </div>
                    <input
                      type="number"
                      step="0.0001"
                      required={!useDirectTrans220Loss}
                      disabled={useDirectTrans220Loss}
                      value={trans220NoLoad}
                      onChange={(e) => setTrans220NoLoad(e.target.value)}
                      placeholder="0.0375"
                      className={
                        useDirectTrans220Loss
                          ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200"
                          : ""
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-1 text-slate-700">
                    <div className="flex items-center gap-1.5 h-4 mb-1">
                      <span
                        className={`text-[10px] font-bold uppercase truncate select-none ${useDirectTrans220Loss ? "text-slate-400" : "text-[#0c2340]"
                          }`}
                      >
                        Load Loss (MW)
                      </span>
                    </div>
                    <input
                      type="number"
                      step="0.0001"
                      required={!useDirectTrans220Loss}
                      disabled={useDirectTrans220Loss}
                      value={trans220Load}
                      onChange={(e) => setTrans220Load(e.target.value)}
                      placeholder="0.3375"
                      className={
                        useDirectTrans220Loss
                          ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200"
                          : ""
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-1 text-slate-700">
                    <div className="flex items-center gap-1.5 h-4 mb-1">
                      <input
                        type="checkbox"
                        checked={useDirectTrans220Loss}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setUseDirectTrans220Loss(checked);
                          if (!checked) setTransLoss220kv("");
                        }}
                        className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer rounded shrink-0"
                      />
                      <span
                        onClick={() => {
                          const checked = !useDirectTrans220Loss;
                          setUseDirectTrans220Loss(checked);
                          if (!checked) setTransLoss220kv("");
                        }}
                        className={`text-[10px] font-bold uppercase cursor-pointer select-none truncate ${!useDirectTrans220Loss ? "text-slate-400" : "text-[#0c2340]"
                          }`}
                      >
                        Loss (%)
                      </span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      disabled={!useDirectTrans220Loss}
                      required={useDirectTrans220Loss}
                      value={transLoss220kv}
                      onChange={(e) => setTransLoss220kv(e.target.value)}
                      placeholder="0.50"
                      className={
                        !useDirectTrans220Loss
                          ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200"
                          : ""
                      }
                    />
                  </div>
                </div>
              </div>
            )}

            {has400Transformer && (
              <div className="space-y-2">
                <h5 className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">
                  {projectValues.tl_volt_stage_2 || 400}kV Transformer
                </h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="flex flex-col gap-1 text-slate-700">
                    <div className="flex items-center gap-1.5 h-4 mb-1">
                      <span
                        className={`text-[10px] font-bold uppercase truncate select-none ${useDirectTrans400Loss ? "text-slate-400" : "text-[#0c2340]"
                          }`}
                      >
                        Capacity (MWh)
                      </span>
                    </div>
                    <input
                      type="number"
                      step="0.0001"
                      required={!useDirectTrans400Loss}
                      disabled={useDirectTrans400Loss}
                      value={trans400Capacity}
                      onChange={(e) => setTrans400Capacity(e.target.value)}
                      placeholder="120.0"
                      className={
                        useDirectTrans400Loss
                          ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200"
                          : ""
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-1 text-slate-700">
                    <div className="flex items-center gap-1.5 h-4 mb-1">
                      <span
                        className={`text-[10px] font-bold uppercase truncate select-none ${useDirectTrans400Loss ? "text-slate-400" : "text-[#0c2340]"
                          }`}
                      >
                        No-Load (MW)
                      </span>
                    </div>
                    <input
                      type="number"
                      step="0.0001"
                      required={!useDirectTrans400Loss}
                      disabled={useDirectTrans400Loss}
                      value={trans400NoLoad}
                      onChange={(e) => setTrans400NoLoad(e.target.value)}
                      placeholder="0.12"
                      className={
                        useDirectTrans400Loss
                          ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200"
                          : ""
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-1 text-slate-700">
                    <div className="flex items-center gap-1.5 h-4 mb-1">
                      <span
                        className={`text-[10px] font-bold uppercase truncate select-none ${useDirectTrans400Loss ? "text-slate-400" : "text-[#0c2340]"
                          }`}
                      >
                        Load Loss (MW)
                      </span>
                    </div>
                    <input
                      type="number"
                      step="0.0001"
                      required={!useDirectTrans400Loss}
                      disabled={useDirectTrans400Loss}
                      value={trans400Load}
                      onChange={(e) => setTrans400Load(e.target.value)}
                      placeholder="1.08"
                      className={
                        useDirectTrans400Loss
                          ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200"
                          : ""
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-1 text-slate-700">
                    <div className="flex items-center gap-1.5 h-4 mb-1">
                      <input
                        type="checkbox"
                        checked={useDirectTrans400Loss}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setUseDirectTrans400Loss(checked);
                          if (!checked) setTransLoss400kv("");
                        }}
                        className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer rounded shrink-0"
                      />
                      <span
                        onClick={() => {
                          const checked = !useDirectTrans400Loss;
                          setUseDirectTrans400Loss(checked);
                          if (!checked) setTransLoss400kv("");
                        }}
                        className={`text-[10px] font-bold uppercase cursor-pointer select-none truncate ${!useDirectTrans400Loss ? "text-slate-400" : "text-[#0c2340]"
                          }`}
                      >
                        Loss (%)
                      </span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      disabled={!useDirectTrans400Loss}
                      required={useDirectTrans400Loss}
                      value={transLoss400kv}
                      onChange={(e) => setTransLoss400kv(e.target.value)}
                      placeholder="0.50"
                      className={
                        !useDirectTrans400Loss
                          ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200"
                          : ""
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Unavailability & Evacuation Capacity */}
      <div className="mt-12 space-y-5">
        <h4 className="form-section-title text-lg font-bold text-slate-800 uppercase tracking-wider pb-1">
          Unavailability & Evacuation Settings
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex flex-col gap-1 text-slate-700">
            <label>Solar availability at 33kV (%)</label>
            <input
              type="number"
              step="0.001"
              required
              value={dt33kvPct}
              onChange={(e) => setDt33kvPct(e.target.value)}
              placeholder="99.40"
            />
          </div>
          <div className="flex flex-col gap-1 text-slate-700">
            <label>BESS Availability (%)</label>
            <input
              type="number"
              step="0.0001"
              required
              value={bessUnavailability}
              onChange={(e) => setBessUnavailability(e.target.value)}
              placeholder="1.50"
            />
          </div>
          <div className="flex flex-col gap-1 text-slate-700">
            <label>Grid availability</label>
            <input
              type="number"
              step="0.0001"
              required
              value={gridUnavailability}
              onChange={(e) => setGridUnavailability(e.target.value)}
              placeholder="0.02"
            />
          </div>
          <div className="flex flex-col gap-1 text-slate-700">
            <label>Evacuation Capacity</label>
            <input
              type="number"
              step="0.001"
              required
              value={evacuationCapacity}
              onChange={(e) => setEvacuationCapacity(e.target.value)}
              placeholder="100.0"
            />
          </div>
          <div className="flex flex-col gap-1 text-slate-700">
            <label>BESS Feeder Cap (MVA)</label>
            <input
              type="number"
              step="0.001"
              required
              value={bessFeederCapacity}
              onChange={(e) => setBessFeederCapacity(e.target.value)}
              placeholder="100.0"
            />
          </div>

          <div className="flex flex-col gap-1 text-slate-700">
            <label>Min BESS Stored Energy (%)</label>
            <input
              type="number"
              step="0.01"
              value={minBessStoreEnergy}
              onChange={(e) => setMinBessStoreEnergy(e.target.value)}
              placeholder="0.00"
            />
          </div>

          {isWind && (
            <>
              <div className="flex flex-col gap-1 text-slate-700">
                <label>
                  Wind Total Losses (%) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={windTotalLossesPct}
                  onChange={(e) => setWindTotalLossesPct(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="flex flex-col gap-1 text-slate-700">
                <label>Min Dispatch Power (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={minDispatchPower}
                  onChange={(e) => setMinDispatchPower(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="flex flex-col gap-1 text-slate-700">
                <label>Battery Degradation Cycle Count At</label>
                <Select
                  value={bessDegradation ?? 1}
                  onChange={(val) => setBessDegradation && setBessDegradation(Number(val))}
                  className="bess-antd-select w-full"
                  style={{ height: 38 }}
                  options={[
                    { value: 1, label: "At Actual" },
                    { value: 2, label: "At Factor" },
                  ]}
                />
              </div>
              {Number(bessDegradation ?? 1) === 1 && (
                <div className="flex flex-col gap-1 text-slate-700">
                  <label>Adjusting Factor</label>
                  <input
                    type="number"
                    step="0.0001"
                    min="1"
                    value={adjustingFactor}
                    onChange={(e) => setAdjustingFactor && setAdjustingFactor(e.target.value)}
                    placeholder="1.0000"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>


      <div className="flex justify-between items-center pt-6 border-t border-slate-100 mt-8">
        <button type="button" onClick={onBack} className="bess-btn-secondary">
          Back
        </button>
        <button type="submit" disabled={submitting} className="bess-btn-primary">
          {submitting ? (
            <>
              <LoadingOutlined className="animate-spin" /> Saving...
            </>
          ) : (
            <>Save & Continue →</>
          )}
        </button>
      </div>
    </form>
  );
}
